<template>
  <div class="dino-game-container">
    <div class="game-area" ref="gameArea">
      <canvas ref="canvas" :width="canvasWidth" :height="canvasHeight"></canvas>
      
      <div class="game-ui">
        <div class="score">Score: {{ score }}</div>
        <div class="instructions" v-if="!gameStarted">
          <p>{{ instructionText }}</p>
          <p class="control-hint">Appuie sur ESPACE ou touche l'écran pour sauter !</p>
          <div class="accessibility-hints">
            <p class="hint">🎮 Jeu simple et amusant</p>
            <p class="hint">🏃 Pas de limite de temps</p>
            <p class="hint">🔄 Tu peux recommencer quand tu veux</p>
          </div>
        </div>
        <div class="game-over" v-if="gameOver">
          <p>Game Over !</p>
          <p>Score final: {{ score }}</p>
          <button @click="restartGame" class="restart-button">Recommencer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DinosaurGame',
  props: {
    instructionText: {
      type: String,
      default: 'Aide Flamou à éviter les obstacles pendant le chargement !'
    },
    flamouImage: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      canvas: null,
      ctx: null,
      canvasWidth: 800,
      canvasHeight: 200,
      gameStarted: false,
      gameOver: false,
      score: 0,
      gameSpeed: 5,
      
      // Flamou (player)
      flamou: {
        x: 50,
        y: 150,
        width: 40,
        height: 40,
        velY: 0,
        jumping: false,
        grounded: true,
        jumpPower: 13, // Reduced jump power for more difficulty
        gravity: 0.7, // Increased gravity for faster falling
        color: '#ff6b6b'
      },
      
      // Obstacles
      obstacles: [],
      obstacleSpawnRate: 0.007, // Slightly increased spawn rate for more difficulty
      obstacleTypes: [
        { height: 20, color: '#4285f4' },  // Short
        { height: 30, color: '#34a853' },  // Medium 
        { height: 40, color: '#ea4335' },  // Tall
        { height: 50, color: '#fbbc04' }   // Very tall
      ],
      
      // Ground
      ground: {
        y: 190,
        height: 10
      },
      
      // Game loop
      animationId: null,
      
      // Controls
      keys: {
        space: false
      },
      
      // Touch support
      touchStartY: 0,
      
      // Flamou image
      flamouImg: null
    }
  },
  mounted() {
    this.initGame()
    this.setupEventListeners()
    this.loadFlamouImage()
    // Auto-start game after a short delay
    setTimeout(() => {
      this.startGame()
    }, 1000)
  },
  beforeUnmount() {
    this.stopGame()
    this.removeEventListeners()
  },
  methods: {
    initGame() {
      this.canvas = this.$refs.canvas
      this.ctx = this.canvas.getContext('2d')
      this.resizeCanvas()
      this.resetGame()
      this.gameLoop()
    },
    
    resizeCanvas() {
      const gameArea = this.$refs.gameArea
      const containerWidth = gameArea.offsetWidth
      this.canvasWidth = Math.min(containerWidth, 800)
      this.canvasHeight = 200
      
      // Adjust game elements based on canvas size
      this.ground.y = this.canvasHeight - 10
      this.flamou.y = this.ground.y - this.flamou.height
    },
    
    loadFlamouImage() {
      if (this.flamouImage) {
        this.flamouImg = new Image()
        this.flamouImg.src = this.flamouImage
      }
    },
    
    setupEventListeners() {
      document.addEventListener('keydown', this.handleKeyDown)
      document.addEventListener('keyup', this.handleKeyUp)
      this.canvas.addEventListener('touchstart', this.handleTouchStart, { passive: false })
      this.canvas.addEventListener('touchmove', this.handleTouchMove, { passive: false })
      this.canvas.addEventListener('click', this.handleClick)
      window.addEventListener('resize', this.resizeCanvas)
    },
    
    removeEventListeners() {
      document.removeEventListener('keydown', this.handleKeyDown)
      document.removeEventListener('keyup', this.handleKeyUp)
      this.canvas.removeEventListener('touchstart', this.handleTouchStart)
      this.canvas.removeEventListener('touchmove', this.handleTouchMove)
      this.canvas.removeEventListener('click', this.handleClick)
      window.removeEventListener('resize', this.resizeCanvas)
    },
    
    handleKeyDown(e) {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault()
        this.jump()
      }
    },
    
    handleKeyUp(e) {
      if (e.code === 'Space' || e.key === ' ') {
        this.keys.space = false
      }
    },
    
    handleTouchStart(e) {
      e.preventDefault()
      this.jump()
    },
    
    handleTouchMove(e) {
      e.preventDefault()
    },
    
    handleClick(e) {
      e.preventDefault()
      this.jump()
    },
    
    jump() {
      if (!this.gameStarted) {
        this.startGame()
      }
      
      if (this.flamou.grounded && !this.gameOver) {
        this.flamou.velY = -this.flamou.jumpPower
        this.flamou.jumping = true
        this.flamou.grounded = false
      }
    },
    
    startGame() {
      this.gameStarted = true
      this.gameOver = false
      this.score = 0
      this.obstacles = []
      this.gameSpeed = 5
      this.resetFlamou()
    },
    
    resetGame() {
      this.gameStarted = false
      this.gameOver = false
      this.score = 0
      this.obstacles = []
      this.gameSpeed = 5
      this.resetFlamou()
    },
    
    resetFlamou() {
      this.flamou.x = 50
      this.flamou.y = this.ground.y - this.flamou.height
      this.flamou.velY = 0
      this.flamou.jumping = false
      this.flamou.grounded = true
    },
    
    restartGame() {
      this.resetGame()
      this.startGame()
    },
    
    gameLoop() {
      if (!this.gameOver) {
        this.update()
        this.draw()
        
        if (this.gameStarted) {
          this.score += 1
          
          // More aggressive difficulty progression
          if (this.score % 600 === 0) {
            this.gameSpeed += 0.8
          }
        }
      }
      
      this.animationId = requestAnimationFrame(this.gameLoop)
    },
    
    update() {
      if (!this.gameStarted) return
      
      // Update Flamou physics
      this.updateFlamou()
      
      // Spawn obstacles
      this.spawnObstacles()
      
      // Update obstacles
      this.updateObstacles()
      
      // Check collisions
      this.checkCollisions()
    },
    
    updateFlamou() {
      // Apply gravity
      this.flamou.velY += this.flamou.gravity
      this.flamou.y += this.flamou.velY
      
      // Ground collision
      if (this.flamou.y >= this.ground.y - this.flamou.height) {
        this.flamou.y = this.ground.y - this.flamou.height
        this.flamou.velY = 0
        this.flamou.jumping = false
        this.flamou.grounded = true
      }
    },
    
    spawnObstacles() {
      if (Math.random() < this.obstacleSpawnRate) {
        // Randomly select an obstacle type for varied heights
        const obstacleType = this.obstacleTypes[Math.floor(Math.random() * this.obstacleTypes.length)]
        const obstacle = {
          x: this.canvasWidth,
          y: this.ground.y - obstacleType.height,
          width: 20,
          height: obstacleType.height,
          color: obstacleType.color
        }
        this.obstacles.push(obstacle)
      }
    },
    
    updateObstacles() {
      this.obstacles = this.obstacles.filter(obstacle => {
        obstacle.x -= this.gameSpeed
        return obstacle.x + obstacle.width > 0
      })
    },
    
    checkCollisions() {
      for (let obstacle of this.obstacles) {
        if (this.flamou.x < obstacle.x + obstacle.width &&
            this.flamou.x + this.flamou.width > obstacle.x &&
            this.flamou.y < obstacle.y + obstacle.height &&
            this.flamou.y + this.flamou.height > obstacle.y) {
          this.gameOver = true
          this.stopGame()
          break
        }
      }
    },
    
    draw() {
      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
      
      // Draw ground - simplified solid color
      this.ctx.fillStyle = '#8B4513'
      this.ctx.fillRect(0, this.ground.y, this.canvasWidth, this.ground.height)
      
      // Draw ground line - simplified
      this.ctx.strokeStyle = '#5D2E00'
      this.ctx.lineWidth = 2
      this.ctx.beginPath()
      this.ctx.moveTo(0, this.ground.y)
      this.ctx.lineTo(this.canvasWidth, this.ground.y)
      this.ctx.stroke()
      
      // Draw Flamou
      if (this.flamouImg && this.flamouImg.complete) {
        this.ctx.drawImage(this.flamouImg, this.flamou.x, this.flamou.y, this.flamou.width, this.flamou.height)
      } else {
        // Fallback: draw simple rectangle
        this.ctx.fillStyle = this.flamou.color
        this.ctx.fillRect(this.flamou.x, this.flamou.y, this.flamou.width, this.flamou.height)
        
        // Add simple eyes
        this.ctx.fillStyle = '#000'
        this.ctx.fillRect(this.flamou.x + 8, this.flamou.y + 8, 4, 4)
        this.ctx.fillRect(this.flamou.x + 20, this.flamou.y + 8, 4, 4)
      }
      
      // Draw obstacles - simplified without shading
      this.obstacles.forEach(obstacle => {
        this.ctx.fillStyle = obstacle.color
        this.ctx.fillRect(obstacle.x, obstacle.y, obstacle.width, obstacle.height)
      })
    },
    
    stopGame() {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
      }
    }
  }
}
</script>

<style scoped>
.dino-game-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
}

.game-area {
  position: relative;
  background: #87CEEB; /* Simplified solid color instead of gradient */
  border-radius: 10px;
  padding: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

canvas {
  display: block;
  border: 2px solid #4285f4;
  border-radius: 8px;
  background: #E0F6FF; /* Simplified solid color instead of gradient */
  touch-action: none;
  cursor: pointer;
}

.game-ui {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 15px;
}

.score {
  font-size: 1.2rem;
  font-weight: bold;
  color: #4285f4;
  background: rgba(255, 255, 255, 0.8);
  padding: 5px 10px;
  border-radius: 15px;
  align-self: flex-end;
  backdrop-filter: blur(5px);
}

.instructions {
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 15px;
  margin: auto;
  max-width: 400px;
  backdrop-filter: blur(5px);
}

.instructions p {
  margin: 10px 0;
  color: #333;
  font-size: 1.1rem;
}

.control-hint {
  font-size: 0.9rem !important;
  color: #666 !important;
  font-style: italic;
}

.accessibility-hints {
  margin-top: 15px;
  padding: 10px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 10px;
  border: 1px solid rgba(76, 175, 80, 0.3);
}

.hint {
  margin: 5px 0 !important;
  font-size: 0.9rem !important;
  color: #4caf50 !important;
  font-weight: 500 !important;
}

.game-over {
  text-align: center;
  background: rgba(244, 67, 54, 0.9);
  color: white;
  padding: 20px;
  border-radius: 15px;
  margin: auto;
  max-width: 300px;
  backdrop-filter: blur(5px);
}

.game-over p {
  margin: 10px 0;
  font-size: 1.1rem;
}

.restart-button {
  background: #fff;
  color: #f44336;
  border: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;
  pointer-events: auto;
  margin-top: 10px;
}

.restart-button:hover {
  background: #f0f0f0;
  transform: translateY(-2px);
}

.restart-button:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .dino-game-container {
    padding: 0 10px;
  }
  
  .game-area {
    padding: 5px;
  }
  
  .score {
    font-size: 1rem;
  }
  
  .instructions {
    max-width: 300px;
    padding: 15px;
  }
  
  .instructions p {
    font-size: 1rem;
  }
}
</style>